import * as React from "react";
import { isEmpty } from "ramda";
import { Box, Divider, Stack, Text } from "@chakra-ui/react";

import SafeDate from "utils/SafeDate";
import useApiEndpoint from "hooks/useApiEndpoint";
import { ICreditCheckPerson } from "utils/user";
import GetCreditCheckExternalReportApiEndpoint, {
  TOutput,
} from "utils/creditCheck/api/GetCreditCheckExternalReport";
import Button from "components/Chakra/Button";
import useRefresh from "hooks/useRefresh";
import { ReactFCC } from "src/types/types";
import IconSuccessCircle from "components/Chakra/Icons/SuccessCircle";
import IconErrorTriangle from "components/Chakra/Icons/ErrorTriangle";

interface IProps {
  persons: Array<ICreditCheckPerson>;
}

const RowRecord: ReactFCC<{ createdAtNode: React.ReactNode }> = ({
  createdAtNode,
  children,
}) => (
  <>
    {createdAtNode}
    <Box mt="12px">{children}</Box>
  </>
);

const RowDetail = React.memo<{
  id: number;
  firstName: string;
  lastName: string;
  birthDay: string;
}>(
  ({ id, firstName, lastName, birthDay }) => {
    const [report, setReport] = React.useState<null | TOutput>(null);
    const [isLoading, setLoading] = React.useState<boolean>(true);
    const [error, setError] = React.useState<string | null>(null);
    const [warning, setWarning] = React.useState<string>();
    const { getFreshToken } = useRefresh();

    const { callEndpoint: getExternalReportApi } = useApiEndpoint(
      GetCreditCheckExternalReportApiEndpoint,
    );

    React.useEffect(() => {
      getExternalReportApi({ id })
        .then(({ data }) => {
          // @ts-expect-error replace ts-ignore
          if (data?.status === "error" || data.error) {
            if (data?.msg) {
              return setError(
                data?.msg
                  .replace("platební stránku", "<b>platební stránku</b>")
                  .replace("<a ", "<a target='_blank'"),
              );
            }

            return setError("Momentálně nedokážeme zjistit stav.");
          }
          setWarning(data?.ispis_cee_warning);
          setReport(data);
        })
        .finally(() => {
          setLoading(false);
        });
    }, []);

    const head = (
      <Stack direction="row" align="center" justify="space-between">
        <Stack spacing="0">
          <Text fontWeight="bold" isTruncated>
            {firstName} {lastName}
          </Text>
          <Text>
            Nar.: {birthDay && new SafeDate(birthDay).formatDateAsNumeric()}
          </Text>
        </Stack>
        {!report?.is_spinner && !Boolean(error) && (
          <Button
            variant="outline"
            color="ud-primary"
            onClick={() => {
              const { accessToken } = getFreshToken();
              if (accessToken) {
                // @ts-ignore
                window
                  ?.open(
                    `/fe-api/credit-check/external-report/${id}?accessToken=${encodeURIComponent(
                      accessToken || "",
                    )}&isPdf=1`,
                    "_blank",
                  )
                  .focus();
              }
            }}
          >
            {error}
            Stáhnout PDF
          </Button>
        )}
      </Stack>
    );

    if (isLoading) {
      return (
        <>
          {head}
          <Text variant="small_2" mt="18px">
            Získávání informací...
          </Text>
        </>
      );
    }
    if (!isLoading && error) {
      return (
        <>
          {head}
          <Text variant="small_2" color="ud-primary" textAlign="left" mt="18px">
            <span dangerouslySetInnerHTML={{ __html: error }} />
          </Text>
        </>
      );
    }
    if (!isLoading && !report) {
      return (
        <>
          {head}
          <Text variant="small_2" color="ud-primary" mt="18px">
            Momentálně nedokážeme zjistit stav.
          </Text>
        </>
      );
    }
    const hasNoRecord =
      (report?.executor || []).length +
      (report?.executor_board || []).length +
      (report?.insolvency_register || []).length +
      (report?.insolvency_register_sk || []).length +
      (report?.ispis_cee || []).length +
      (report?.ispis_cee_sk || []).length +
      (report?.police || []).length;

    const createdAtNode = (
      <>
        {head}
        {!warning && (
          <Text mt="12px">
            Prověření proběhlo{" "}
            {report?.renter.created_at &&
              new SafeDate(report?.renter.created_at).formatDateAsNumeric()}
          </Text>
        )}
      </>
    );

    if (hasNoRecord === 0 && !warning) {
      return (
        <RowRecord createdAtNode={createdAtNode}>
          <Stack direction="row" alignItems="center">
            <IconSuccessCircle fillWith="success" />
            <Text variant="small_2" mt="3px">
              Bez záznamu
            </Text>
          </Stack>
        </RowRecord>
      );
    }
    const arr = [
      !isEmpty(report?.debtor_zpmvcr || []) && {
        title: "Zdravotní pojišťovna ministerstva vnitra České republiky",
        total: report?.debtor_zpmvcr.length,
      },
      !isEmpty(report?.debtor_zpvzp || []) && {
        title: "Veřejné zdravotní pojištění",
        total: report?.debtor_zpvzp.length,
      },
      !isEmpty(report?.executor || []) && {
        title: "Centralní evidence exekucí",
        total: report?.executor.length,
      },
      !isEmpty(report?.executor_board || []) && {
        title: "Exekuce",
        total: report?.executor_board.length,
      },
      !isEmpty(report?.insolvency_register || []) && {
        title: "Insolvenční rejstřík",
        total: report?.insolvency_register.length,
      },
      !isEmpty(report?.insolvency_register_sk || []) && {
        title: "Registr úpadcov na Slovensku",
        total: report?.insolvency_register_sk.length,
      },
      !isEmpty(report?.ispis_cee || []) && {
        title: "Centralní evidence exekucí",
        total: report?.ispis_cee.length,
      },
      !isEmpty(report?.ispis_cee_sk || []) && {
        title: "Centralni evidence exekuci SK",
        total: report?.ispis_cee_sk.length,
      },
      !isEmpty(report?.police || []) && {
        title: "Osoby hledané policií",
        total: report?.police.length,
      },
    ].filter(Boolean) as Array<{ title: React.ReactNode; total: number }>;

    const getStatus = (records: number): string => {
      if (records === 1) {
        return "záznam";
      }

      if (records > 4) {
        return "záznamů";
      }

      return "záznamy";
    };

    return (
      <RowRecord createdAtNode={createdAtNode}>
        {warning && (
          <Text variant="small_2" color="ud-primary" mt="3px">
            {warning}
          </Text>
        )}
        <Stack spacing="5px">
          {arr.map((x, i) => (
            <Stack key={i} direction="row" alignItems="center">
              <IconErrorTriangle fillWith="ud-primary" />
              <Text variant="small_2" color="ud-primary" mt="3px">
                {x.title}:{" "}
                <b>
                  {x.total} {getStatus(x.total)}
                </b>
              </Text>
            </Stack>
          ))}
        </Stack>
      </RowRecord>
    );
  },
  (prev, next) => prev.id !== next.id,
);

const PersonList: React.FunctionComponent<IProps> = ({ persons }) => (
  <div>
    <Text variant="large_2" fontWeight="bold" mt="30px" mb="9px">
      Prověřené osoby
    </Text>

    {persons.map((x, i, arr) => (
      <Box key={x.id} pt="15px">
        <RowDetail
          id={x.id}
          firstName={x.firstName}
          lastName={x.lastName}
          birthDay={x.birthDay}
        />
        {arr.length - 1 !== i && <Divider pt="15px" />}
      </Box>
    ))}
  </div>
);

export default PersonList;
